import {
  leadSource,
  leadUrgency,
  type SalesLeadUrgencyTier,
  type SalesOrgLeadCard,
} from "@xop/dashboard-shared";

import { HEAT_RANK } from "../../leads/heatStyles.js";

/**
 * Pure sort logic for the leads table — extracted from `LeadsTable.tsx` so it
 * unit-tests directly (it accepts an injectable `now`, so urgency-dependent
 * ordering is deterministic in tests). `SortKey` / `SortDir` are re-exported by
 * `LeadsTable.tsx`, so external consumers (e.g. `leads/leadSort.ts`) keep
 * importing them from `../components/LeadsTable.js`.
 */

export type SortKey = "heat" | "name" | "country" | "vertical" | "source" | "confidence" | "created" | "last";
export type SortDir = "asc" | "desc";

// Heat-pill colours + the heat RANK order both live in the shared
// `leads/heatStyles.ts` (imported above) so the table, the heat sort, the
// urgency-folded display heat, and the lead-detail hero never drift. The sort
// below ranks on the lead's OWN `heat` (urgency is already the dominant key, so
// the display elevation doesn't need to re-order within a tier).

// Deadline urgency ("super hot mode") is the DOMINANT sort dimension — a lead
// whose dated window is closing floats to the top of every view regardless of
// the column the operator sorted by, so it can't be missed. Higher = more
// urgent (sorts first). `none` leaves the row in its normal column order.
const URGENCY_RANK: Record<SalesLeadUrgencyTier, number> = {
  none: 0, super_hot: 1, last_chance: 2,
};

export function defaultDir(key: SortKey): SortDir {
  // Urgency-like columns default to desc (most-urgent first); alpha/categorical
  // columns default to asc (alphabetical / grouped).
  if (key === "name" || key === "country" || key === "vertical" || key === "source") return "asc";
  return "desc";
}

export function sortLeads(
  items: SalesOrgLeadCard[],
  key: SortKey,
  dir: SortDir,
  now: Date = new Date(),
): SalesOrgLeadCard[] {
  const mult = dir === "asc" ? 1 : -1;
  // Precompute each lead's urgency once (the object identity is the map key).
  const urg = new Map(items.map((l) => [l, leadUrgency(l, now)]));
  const cmp = (a: SalesOrgLeadCard, b: SalesOrgLeadCard): number => {
    // Primary, always: deadline urgency. Most-urgent tier first, then soonest
    // deadline first — independent of the chosen column + direction.
    const ua = urg.get(a)!;
    const ub = urg.get(b)!;
    const ru = URGENCY_RANK[ub.tier] - URGENCY_RANK[ua.tier];
    if (ru !== 0) return ru;
    if (ua.tier !== "none" && ua.daysLeft != null && ub.daysLeft != null) {
      const dd = ua.daysLeft - ub.daysLeft;
      if (dd !== 0) return dd;
    }
    let d = 0;
    switch (key) {
      case "heat":
        d = (HEAT_RANK[a.heat] ?? 0) - (HEAT_RANK[b.heat] ?? 0);
        break;
      case "name":
        d = a.name.localeCompare(b.name);
        break;
      case "country":
        d = (a.country || "").localeCompare(b.country || "");
        break;
      case "vertical":
        d = (a.vertical || "").localeCompare(b.vertical || "");
        break;
      case "source":
        // Which bot found it — group by the normalized source (discovery /
        // harvester). `leadSource` folds any missing/legacy value to discovery.
        d = leadSource(a.source).localeCompare(leadSource(b.source));
        break;
      case "confidence":
        d = a.confidence - b.confidence;
        break;
      case "created": {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        d = ta - tb;
        break;
      }
      case "last": {
        const ta = a.last_signal_at ? new Date(a.last_signal_at).getTime() : 0;
        const tb = b.last_signal_at ? new Date(b.last_signal_at).getTime() : 0;
        d = ta - tb;
        break;
      }
    }
    if (d !== 0) return d * mult;
    // Stable tiebreaker: confidence desc, then name asc — gives a
    // consistent secondary ordering regardless of the primary key.
    const c = b.confidence - a.confidence;
    if (c !== 0) return c;
    return a.name.localeCompare(b.name);
  };
  return [...items].sort(cmp);
}

/** Absolute local timestamp for the Created cell's hover tooltip — the
 * "2h ago" stamp answers "recent?" at a glance; the tooltip answers
 * "exactly when?" without spending a column on it. Empty (no tooltip) for
 * a missing/invalid date. */
export function absoluteTitle(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}
